import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { productsAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useProductTranslation } from '../hooks/useProductTranslation';
import { convertPrice, getCurrencyByLanguage, getCurrencySymbol } from '../utils/currency';
import { toast } from 'react-toastify';
import SEO from '../components/SEO';
import { ProductSkeletonGrid } from '../components/LoadingSkeletons/ProductSkeleton';
import { Spinner } from '../components/LoadingSkeletons/Spinner';

const Products = () => {
  const { t, language } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();

  const isSearchActive = searchInput.trim().length > 0;
  const isDropdownActive = Boolean(selectedManufacturer || selectedModel || selectedYear);
  const hasActiveFilters = Boolean(searchQuery || searchInput || selectedManufacturer || selectedModel || selectedYear);

  const category = searchParams.get('category');
  const manufacturer = searchParams.get('manufacturer');
  const search = searchParams.get('search');

  const PRODUCTS_PER_PAGE = 12;

  useEffect(() => {
    setPage(1);
    setHasMore(true);
  }, [category, manufacturer, search, selectedManufacturer, selectedModel, selectedYear, searchQuery]);

  useEffect(() => {
    fetchProducts(1, true);
  }, [category, manufacturer, search, selectedManufacturer, selectedModel, selectedYear, searchQuery]);

  useEffect(() => {
    setSearchQuery(search || '');
    setSearchInput(search || '');
  }, [search]);

  useEffect(() => {
    const fetchManufacturers = async () => {
      try {
        const response = await productsAPI.getFitmentManufacturers();
        setManufacturers(response.data || []);
      } catch (error) {
        console.error('Error fetching fitment manufacturers:', error);
      }
    };
    fetchManufacturers();
  }, []);

  useEffect(() => {
    const fetchModels = async () => {
      if (!selectedManufacturer) {
        setModels([]);
        setSelectedModel('');
        return;
      }
      try {
        const response = await productsAPI.getFitmentModels(selectedManufacturer);
        setModels(response.data || []);
      } catch (error) {
        console.error('Error fetching fitment models:', error);
      }
    };
    fetchModels();
  }, [selectedManufacturer]);

  useEffect(() => {
    const fetchYears = async () => {
      if (!selectedManufacturer || !selectedModel) {
        setYears([]);
        setSelectedYear('');
        return;
      }
      try {
        const response = await productsAPI.getFitmentYears(selectedManufacturer, selectedModel);
        setYears(response.data || []);
      } catch (error) {
        console.error('Error fetching fitment years:', error);
      }
    };
    fetchYears();
  }, [selectedManufacturer, selectedModel]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
        loadMoreProducts();
      }
    }, { threshold: 0.1 });

    const sentinel = document.getElementById('products-sentinel');
    if (sentinel) observer.observe(sentinel);

    return () => {
      if (sentinel) observer.unobserve(sentinel);
    };
  }, [hasMore, loadingMore, loading, page]);

  const fetchProducts = async (pageNum, reset = false) => {
    try {
      if (reset) setLoading(true);
      else setLoadingMore(true);

      const usingFitment = selectedManufacturer || selectedModel || selectedYear;
      let allProducts = [];

      if (usingFitment) {
        const params = {
          manufacturer: selectedManufacturer || undefined,
          model: selectedModel || undefined,
          year: selectedYear || undefined,
          search: searchQuery || undefined,
        };
        const response = await productsAPI.searchFitment(params);
        allProducts = response.data || [];
      } else {
        const params = {};
        if (category) params.category = category;
        if (manufacturer) params.manufacturer = manufacturer;
        if (searchQuery) params.search = searchQuery;

        const response = await productsAPI.getAll(params);
        allProducts = (response.data || []).filter(product => product.category !== 'handheld_tuners');
      }

      // Paginate the results
      const startIdx = (pageNum - 1) * PRODUCTS_PER_PAGE;
      const endIdx = startIdx + PRODUCTS_PER_PAGE;
      const paginatedProducts = allProducts.slice(startIdx, endIdx);

      if (reset) {
        setProducts(paginatedProducts);
      } else {
        setProducts(prev => [...prev, ...paginatedProducts]);
      }

      // Check if there are more products
      setHasMore(endIdx < allProducts.length);
      setPage(pageNum);
    } catch (error) {
      toast.error(t('toast.failedToLoad'), { autoClose: 1000 });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreProducts = () => {
    fetchProducts(page + 1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchQuery('');
    setSelectedManufacturer('');
    setSelectedModel('');
    setSelectedYear('');
  };

  const handleAddToCart = async (productId) => {
    if (!isAuthenticated()) {
      toast.error(t('toast.loginRequired'), { autoClose: 1000 });
      return;
    }
    try {
      await addToCart(productId, 1);
    } catch (error) {
      // Error handled in context
    }
  };

  if (loading) {
    return (
      <>
        <SEO
          title={t('seo.products.title')}
          description={t('seo.products.shortDescription')}
        />
        <div className="container-custom py-12">
          <div className="h-10 bg-gray-200 rounded w-1/3 mb-8 animate-pulse"></div>
          <ProductSkeletonGrid count={6} />
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={t('seo.products.title')}
        description={t('seo.products.description')}
        keywords={t('seo.products.keywords')}
        type="website"
      />

      <div className="container-custom py-12">
        <h1 className="text-4xl font-display font-bold mb-8">
          {search ? `${t('products.searchResultsFor')} "${search}"` : category ? category.replace('_', ' ').toUpperCase() : manufacturer ? manufacturer : t('products.allProducts')}
        </h1>

        <form onSubmit={handleSearch} className="mb-8">
          <div className="mb-4">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                if (e.target.value) {
                  setSelectedManufacturer('');
                  setSelectedModel('');
                  setSelectedYear('');
                }
              }}
              placeholder={t('home.searchPlaceholder')}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-racing-red"
              minLength={2}
              disabled={isDropdownActive}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <select
              value={selectedManufacturer}
              onChange={(e) => {
                setSelectedManufacturer(e.target.value);
                setSelectedModel('');
                setSelectedYear('');
                if (e.target.value) {
                  setSearchInput('');
                  setSearchQuery('');
                }
              }}
              className="px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-racing-red"
              disabled={isSearchActive}
            >
              <option value="">{t('home.selectManufacturer')}</option>
              {manufacturers.map((mfr) => (
                <option key={mfr} value={mfr}>{mfr}</option>
              ))}
            </select>

            <select
              value={selectedModel}
              onChange={(e) => {
                setSelectedModel(e.target.value);
                setSelectedYear('');
              }}
              disabled={isSearchActive || !selectedManufacturer}
              className="px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-racing-red disabled:bg-gray-100"
            >
              <option value="">{t('home.selectModel')}</option>
              {models.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              disabled={isSearchActive || !selectedModel}
              className="px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-racing-red disabled:bg-gray-100"
            >
              <option value="">{t('home.selectYear')}</option>
              {years.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="mt-4 flex items-center gap-4">
            <button
              type="submit"
              className="px-6 py-2 bg-racing-red text-white rounded-lg font-semibold hover:bg-red-700 transition-colors"
            >
              {t('home.searchButton')}
            </button>
            {(searchQuery || selectedManufacturer || selectedModel || selectedYear) && (
              <button
                type="button"
                onClick={handleClear}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </form>

        {products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600 mb-4">{t('products.noProductsFound')}</p>
            {hasActiveFilters && (
              <p className="text-gray-500 mb-6">
                {t('products.tryDifferentKeywords')}
              </p>
            )}
            <Link 
              to="/contact" 
              className="inline-block px-6 py-3 bg-racing-red text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('nav.contact')}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} t={t} language={language} handleAddToCart={handleAddToCart} />
            ))}
          </div>
        )}

        {/* Infinite scroll sentinel */}
        {hasMore && products.length > 0 && (
          <div id="products-sentinel" className="flex justify-center py-8">
            {loadingMore && (
              <div className="spinner"></div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

// Product Card Component - allows use of hooks
const ProductCard = ({ product, t, language, handleAddToCart }) => {
  const translatedProduct = useProductTranslation(product);
  const currency = getCurrencyByLanguage(language);
  const convertedPrice = convertPrice(product.price, currency);
  const symbol = getCurrencySymbol(currency);

  return (
    <div className="card flex flex-col h-full">
      <Link to={`/products/${product.slug}`}>
        <div className="h-48 md:h-56 bg-gradient-to-br from-gray-200 to-gray-300 overflow-hidden relative">
          {product.image_url && (
            <img 
              src={product.image_url} 
              alt={translatedProduct.name}
              className="w-full h-full object-contain hover:scale-105 transition duration-300"
              loading="lazy"
            />
          )}
        </div>
      </Link>
      <div className="p-4 flex flex-col flex-grow">
        <Link to={`/products/${product.slug}`}>
          <h3 className="font-bold text-lg mb-2 hover:text-racing-red transition h-14 line-clamp-2">
            {translatedProduct.name}
          </h3>
        </Link>
        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
          {translatedProduct.short_description}
        </p>
        <div className="flex items-center justify-between mt-auto">
          <span className="text-2xl font-bold text-racing-red">
            {symbol}{convertedPrice}
          </span>
          <button
            onClick={() => handleAddToCart(product.id)}
            className="btn btn-primary text-sm py-1 px-1"
          >
            {t('common.addToCart')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Products;
